// ==========================================
// Buddy panel (3a) — the full assistant
// ==========================================
//   title bar        30px   drag · pin · collapse
//   xp rail           3px   full-bleed, gradient
//   bb header        52px   36px mascot + level ring, streak pill
//   chat log        flex    bubbles, workflow card, TTS chip, reward chip
//   chip rail        34px   3-5 workflows, horizontal scroll, +N
//   input row        52px   field · TTS toggle · mic
//   habitat sheet   full    slides up over chat
//
// Permanent gamification chrome is exactly three things — the 3px rail, the level
// number in the header and the streak pill — which is the <=46px budget from 2d.
// ==========================================

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BbState, BuddyMessage, BuddyProgress, Density, Workflow, WorkflowRun } from '../models/buddy.types';
import { ACCENT_HEX, MIC_SILENCE_MS, TRACK_HEX } from '../models/buddy.constants';
import { COMPANION_NAME, MOCK_TRANSCRIPT } from '../services/buddy-mock.data';
import { BbStateService } from '../services/bb-state.service';
import { BuddyChatService } from '../services/buddy-chat.service';
import { BuddyProgressService } from '../services/buddy-progress.service';
import { BuddyStateService } from '../services/buddy-state.service';
import { BuddyWorkflowService } from '../services/buddy-workflow.service';
import { BuddyChatLogComponent } from '../components/buddy-chat-log/buddy-chat-log.component';
import { BuddyInputRowComponent } from '../components/buddy-input-row/buddy-input-row.component';

/** How quickly the simulated transcript reveals itself. */
const TRANSCRIPT_WORD_MS = 180;

@Component({
  selector: 'app-buddy-panel',
  standalone: false,
  templateUrl: './buddy-panel.component.html',
  styleUrls: ['./buddy-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuddyPanelComponent implements OnInit, OnDestroy {
  @Input() density: Density = 'default';

  @Output() dragStarted = new EventEmitter<PointerEvent>();
  @Output() collapseRequested = new EventEmitter<void>();
  @Output() edgeToggleRequested = new EventEmitter<void>();
  @Output() layoutToggleRequested = new EventEmitter<void>();

  @ViewChild(BuddyChatLogComponent) chatLog?: BuddyChatLogComponent;
  @ViewChild(BuddyInputRowComponent) inputRow?: BuddyInputRowComponent;

  readonly companionName = COMPANION_NAME;
  /** Level ring around BB's head: brand orange on the gray-200 track. */
  readonly xpColor = ACCENT_HEX.xp;
  readonly trackColor = TRACK_HEX;

  messages: readonly BuddyMessage[] = [];
  runs: Record<string, WorkflowRun> = {};
  progress!: BuddyProgress;
  bbState: BbState = 'idle';
  ttsEnabled = true;
  streaming = false;
  paletteOpen = false;
  habitatOpen = false;
  historyOpen = false;
  /** Which collapsed form Buddy takes: 4a's corner bubble or its edge tab. */
  useEdgeTab = false;

  micActive = false;
  handsFree = false;
  transcript = '';
  /** Non-empty while the user is typing a "/" filter. */
  paletteQuery = '';

  private readonly destroyed$ = new Subject<void>();
  private transcriptTimer: ReturnType<typeof setTimeout> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Chat scroll offset stashed when the habitat sheet opens, restored on back (3d). */
  private stashedScroll = 0;

  constructor(
    private readonly chat: BuddyChatService,
    private readonly workflows: BuddyWorkflowService,
    private readonly progressService: BuddyProgressService,
    private readonly shell: BuddyStateService,
    private readonly bb: BbStateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.progress = this.progressService.current;
  }

  ngOnInit(): void {
    this.chat.messages.pipe(takeUntil(this.destroyed$)).subscribe((m) => {
      this.messages = m;
      this.cdr.markForCheck();
    });
    this.chat.isStreaming.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.streaming = s;
      this.cdr.markForCheck();
    });
    this.workflows.runs.pipe(takeUntil(this.destroyed$)).subscribe((r) => {
      this.runs = r;
      this.cdr.markForCheck();
    });
    // Re-render the rail + palette once the workflows load from the backend.
    this.workflows.workflows$.pipe(takeUntil(this.destroyed$)).subscribe(() => this.cdr.markForCheck());
    this.progressService.progress.pipe(takeUntil(this.destroyed$)).subscribe((p) => {
      this.progress = p;
      this.cdr.markForCheck();
    });
    this.bb.state.pipe(takeUntil(this.destroyed$)).subscribe((s) => {
      this.bbState = s;
      this.cdr.markForCheck();
    });
    this.shell.ttsEnabled.pipe(takeUntil(this.destroyed$)).subscribe((t) => {
      this.ttsEnabled = t;
      this.cdr.markForCheck();
    });
    this.shell.paletteOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.paletteOpen = o;
      this.cdr.markForCheck();
    });
    this.shell.historyOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.historyOpen = o;
      this.cdr.markForCheck();
    });
    this.shell.useEdgeTab.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
      this.useEdgeTab = v;
      this.cdr.markForCheck();
    });
    this.shell.habitatOpen.pipe(takeUntil(this.destroyed$)).subscribe((o) => {
      this.onHabitatToggled(o);
    });
  }

  // ------------------------------------------
  // Derived view state
  // ------------------------------------------

  get xpPct(): number {
    if (!this.progress.xpForLevel) {
      return 0;
    }
    return Math.min(100, Math.round((this.progress.xpIntoLevel / this.progress.xpForLevel) * 100));
  }

  /** Level ring around BB's head in the panel header. */
  get ringTurns(): number {
    return this.xpPct / 100;
  }

  get levelLine(): string {
    return `${this.progress.stageLabel} · LVL ${this.progress.level} · ${this.progress.xpIntoLevel}/${this.progress.xpForLevel}`;
  }

  get compactLevelLine(): string {
    return `LVL ${this.progress.level} · ${this.progress.xpIntoLevel}/${this.progress.xpForLevel}`;
  }

  get streakLabel(): string {
    return `${this.progress.streakDays}d`;
  }

  get railWorkflows(): readonly Workflow[] {
    return this.workflows.railWorkflows;
  }

  get overflowCount(): number {
    return this.workflows.overflowCount;
  }

  get paletteWorkflows(): readonly Workflow[] {
    return this.workflows.filter(this.paletteQuery);
  }

  /** The chip rail collapses into the input row's ⌗ button at the compact density (3c). */
  get showChipRail(): boolean {
    return this.density !== 'compact';
  }

  /** The daily-quest strip only appears at the comfortable density (3c). */
  get showQuestStrip(): boolean {
    return this.density === 'comfortable';
  }

  get questPct(): number {
    const q = this.progress.quest;
    return q.total ? Math.round((q.done / q.total) * 100) : 0;
  }

  /** BB reads the newest assistant message aloud while `speaking`. */
  get speakingMessageId(): string | null {
    if (this.bbState !== 'speaking') {
      return null;
    }
    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      if (this.messages[i].role === 'assistant') {
        return this.messages[i].id;
      }
    }
    return null;
  }

  // ------------------------------------------
  // Chat
  // ------------------------------------------

  onSubmit(text: string): void {
    // "/" on its own opens the palette rather than sending.
    if (text.startsWith('/')) {
      this.shell.setPaletteOpen(true);
      return;
    }
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
    this.chat.send(text);
  }

  onQueryChanged(value: string): void {
    // Typing "/" filters the workflow list inline (3d).
    if (value.startsWith('/')) {
      this.paletteQuery = value;
      this.shell.setPaletteOpen(true);
    } else if (this.paletteQuery) {
      this.paletteQuery = '';
      this.shell.setPaletteOpen(false);
    }
    this.bb.noteActivity();
  }

  onStopStream(): void {
    this.chat.stop();
  }

  onSuggestion(label: string): void {
    this.chat.send(label);
  }

  // ------------------------------------------
  // Voice
  // ------------------------------------------

  onMicToggled(): void {
    if (this.micActive) {
      this.commitTranscript();
    } else {
      this.startListening(false);
    }
  }

  onMicHeld(): void {
    this.startListening(true);
  }

  onTtsToggled(): void {
    const on = this.shell.toggleTts();
    if (!on && this.bbState === 'speaking') {
      this.bb.settle();
    }
  }

  onStopTts(): void {
    this.bb.settle();
  }

  private startListening(handsFree: boolean): void {
    this.micActive = true;
    this.handsFree = handsFree;
    this.transcript = '';
    this.bb.wake();
    this.bb.listen();

    // API-SWAP: replace this reveal with the Web Speech API's interim transcript.
    const words = MOCK_TRANSCRIPT.split(' ');
    let index = 0;
    const tick = (): void => {
      if (index >= words.length) {
        this.transcriptTimer = null;
        if (handsFree) {
          // Hands-free stops itself after 1.5s of silence.
          this.silenceTimer = setTimeout(() => this.commitTranscript(), MIC_SILENCE_MS);
        }
        this.cdr.markForCheck();
        return;
      }
      this.transcript = words.slice(0, index + 1).join(' ');
      index += 1;
      this.cdr.markForCheck();
      this.transcriptTimer = setTimeout(tick, TRANSCRIPT_WORD_MS);
    };
    this.transcriptTimer = setTimeout(tick, TRANSCRIPT_WORD_MS);
  }

  private commitTranscript(): void {
    this.clearVoiceTimers();
    const text = this.transcript.trim();
    this.micActive = false;
    this.handsFree = false;
    this.transcript = '';
    this.cdr.markForCheck();

    if (text) {
      this.chat.send(text);
    } else {
      this.bb.settle();
    }
  }

  // ------------------------------------------
  // Workflows
  // ------------------------------------------

  onRunWorkflow(workflowId: string): void {
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
    this.inputRow?.setValue('');
    this.workflows.run(workflowId);
  }

  onWorkflowParams(workflowId: string): void {
    const workflow = this.workflows.byId(workflowId);
    if (workflow) {
      // Parameters land in the field so they can be edited before running.
      this.inputRow?.setValue(`/${workflow.name} `);
      this.inputRow?.focus();
    }
  }

  onCancelRun(runId: string): void {
    this.workflows.cancel(runId);
  }

  onRetryRun(runId: string): void {
    this.workflows.retry(runId);
  }

  onDecideRun(payload: { runId: string; approved: boolean }): void {
    this.workflows.decide(payload.runId, payload.approved);
  }

  onDefineWorkflow(): void {
    this.shell.setPaletteOpen(false);
    const award = this.progressService.awardDefineWorkflow();
    this.chat.appendSystem('Recording your next exchange as a reusable workflow.');
    if (award.leveledUp) {
      this.bb.celebrate();
      this.chat.appendMilestone(`Level ${award.newLevel} — BB grows into it.`);
    } else {
      this.bb.succeed();
    }
  }

  onTogglePalette(): void {
    this.shell.togglePalette();
  }

  // ------------------------------------------
  // History
  // ------------------------------------------

  onToggleHistory(): void {
    this.shell.toggleHistory();
  }

  onCloseHistory(): void {
    this.shell.setHistoryOpen(false);
  }

  /** Swap between 4a's corner bubble and its edge tab. */
  onToggleCollapseStyle(): void {
    this.shell.toggleCollapseStyle();
  }

  get collapseStyleLabel(): string {
    return this.useEdgeTab ? 'Collapse to a corner bubble instead' : 'Collapse to an edge tab instead';
  }

  onClosePalette(): void {
    this.shell.setPaletteOpen(false);
    this.paletteQuery = '';
  }

  // ------------------------------------------
  // Habitat
  // ------------------------------------------

  onOpenHabitat(): void {
    this.shell.openHabitat();
  }

  onCloseHabitat(): void {
    this.shell.closeHabitat();
  }

  private onHabitatToggled(open: boolean): void {
    if (open && !this.habitatOpen) {
      this.stashedScroll = this.chatLog?.scrollTop ?? 0;
    }
    const wasOpen = this.habitatOpen;
    this.habitatOpen = open;
    this.cdr.markForCheck();

    if (wasOpen && !open) {
      // Back returns to the exact scroll position (3d).
      setTimeout(() => this.chatLog?.restoreScroll(this.stashedScroll));
    }
  }

  // ------------------------------------------
  // Chrome
  // ------------------------------------------

  onTitleBarPointerDown(event: PointerEvent): void {
    this.dragStarted.emit(event);
  }

  private clearVoiceTimers(): void {
    if (this.transcriptTimer !== null) {
      clearTimeout(this.transcriptTimer);
      this.transcriptTimer = null;
    }
    if (this.silenceTimer !== null) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearVoiceTimers();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
